import React, { useState, useEffect } from 'react';
import { Smartphone, Palette, Clock, Zap } from 'lucide-react';

const appScreenshots = [
  "https://customer-assets.emergentagent.com/job_brand-story-hub-9/artifacts/57rs3k4x_IMG_2368.JPEG",
  "https://customer-assets.emergentagent.com/job_brand-story-hub-9/artifacts/ww7nk3gq_IMG_2369.JPEG",
  "https://customer-assets.emergentagent.com/job_brand-story-hub-9/artifacts/o2hjdf5t_IMG_2370.JPEG",
  "https://customer-assets.emergentagent.com/job_brand-story-hub-9/artifacts/bu3us589_IMG_2372.JPEG",
  "https://customer-assets.emergentagent.com/job_brand-story-hub-9/artifacts/43md3vo0_IMG_2374.JPEG",
];

const appFeatures = [
  {
    icon: Palette,
    title: "Unlimited Colors",
    description: "Choose from millions of colors to match any mood or occasion"
  },
  {
    icon: Clock,
    title: "Scheduling",
    description: "Set timers and schedules for automated lighting control"
  },
  {
    icon: Zap,
    title: "Preset Scenes",
    description: "Save and recall your favorite lighting configurations"
  },
  {
    icon: Smartphone,
    title: "Easy Control",
    description: "Control all your lights from your smartphone"
  }
];

const AppShowcase = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % appScreenshots.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="py-20 sm:py-24 md:py-32 bg-gradient-to-b from-background to-muted/30" data-testid="app-showcase-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left - App Screenshots */}
          <div className="relative flex justify-center order-2 lg:order-1">
            <div className="relative">
              {/* Phone frame mockup */}
              <div className="relative w-[280px] sm:w-[320px] h-[560px] sm:h-[640px] bg-gray-900 rounded-[3rem] p-3 shadow-2xl">
                {/* Screen bezel */}
                <div className="relative w-full h-full bg-black rounded-[2.5rem] overflow-hidden">
                  {/* Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-black rounded-b-2xl z-10" />
                  
                  {/* App Screenshots Slideshow */}
                  <div className="relative w-full h-full">
                    {appScreenshots.map((src, index) => (
                      <img
                        key={`app-slide-${index}`}
                        src={src}
                        alt={`Illumidel App Screenshot ${index + 1}`}
                        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                          index === currentIndex ? 'opacity-100' : 'opacity-0'
                        }`}
                      />
                    ))}
                  </div>
                </div>
                
                {/* Home indicator */}
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-32 h-1 bg-gray-600 rounded-full" />
              </div>
              
              {/* Glow effect behind phone */}
              <div className="absolute inset-0 -z-10 bg-gradient-to-br from-primary/30 via-blue-500/20 to-purple-500/30 blur-3xl rounded-full scale-110" />
            </div>
            
            {/* Slideshow indicators */}
            <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 flex gap-2">
              {appScreenshots.map((_, index) => (
                <button
                  key={`app-indicator-${index}`}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index === currentIndex 
                      ? 'bg-primary w-6' 
                      : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Right - Content */}
          <div className="order-1 lg:order-2">
            <span className="text-xs font-semibold tracking-widest uppercase text-primary mb-4 block">
              Control At Your Fingertips
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6 tracking-tight">
              The Illumidel App
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground mb-10 font-light leading-relaxed">
              Take complete control of your lighting system with the powerful Illumidel app. 
              Create custom scenes, schedule automations, and transform your space with just a tap.
            </p>

            {/* Features Grid */}
            <div className="grid grid-cols-2 gap-6">
              {appFeatures.map((feature) => (
                <div key={feature.title} className="group">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-3 group-hover:bg-primary/20 transition-colors duration-300">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-1">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground font-light">{feature.description}</p>
                </div>
              ))}
            </div>

            {/* App Store Buttons */}
            <div className="mt-10 flex flex-wrap gap-4">
              <a 
                href="#" 
                className="inline-flex items-center gap-3 px-6 py-3 bg-foreground text-background rounded-xl hover:opacity-90 transition-opacity"
              >
                <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09997 22C7.78997 22.05 6.79997 20.68 5.95997 19.47C4.24997 17 2.93997 12.45 4.69997 9.39C5.56997 7.87 7.12997 6.91 8.81997 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z"/>
                </svg>
                <div className="text-left">
                  <div className="text-[10px] opacity-70">Download on the</div>
                  <div className="text-sm font-semibold -mt-0.5">App Store</div>
                </div>
              </a>
              <a 
                href="#" 
                className="inline-flex items-center gap-3 px-6 py-3 bg-foreground text-background rounded-xl hover:opacity-90 transition-opacity"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 0 1 0 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.802 8.99l-2.303 2.303-8.635-8.635z"/>
                </svg>
                <div className="text-left">
                  <div className="text-[10px] opacity-70">Get it on</div>
                  <div className="text-sm font-semibold -mt-0.5">Google Play</div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AppShowcase;
